import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useCurrentUser } from "@api/hooks";
import { getAccessToken, getStoredUser, clearAuthCookies } from "@utils/cookies";
import { useRoleTest } from "../RoleTestContext";
import Modal from "./Modal";
import Button from "../ui/Button";

const sortedClinics = (clinic) => [...(clinic || [])].sort().join(",");

/**
 * The user cookie is a snapshot taken at login, so a role/clinic/status change
 * made by an organizer doesn't apply until the user logs in again. Compare the
 * snapshot against /auth/me/ and ask the user to log out when they differ.
 */
export default function PermissionsChangedAlert() {
  // useLocation re-renders this on navigation, so the token check picks up a
  // fresh login without a page reload.
  useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setTestRoleId } = useRoleTest();
  const [dismissed, setDismissed] = useState(false);

  const isLoggedIn = !!getAccessToken();
  const { data: serverUser } = useCurrentUser({ enabled: isLoggedIn });
  const storedUser = getStoredUser();

  const changed =
    isLoggedIn &&
    !!serverUser &&
    !!storedUser &&
    serverUser.id === storedUser.id &&
    (serverUser.role !== storedUser.role ||
      serverUser.status !== storedUser.status ||
      sortedClinics(serverUser.clinic) !== sortedClinics(storedUser.clinic));

  if (!changed || dismissed) return null;

  function handleLogout() {
    setTestRoleId(null);
    clearAuthCookies();
    queryClient.clear();
    navigate("/login", { replace: true });
  }

  return (
    <Modal
      isOpen
      onClose={() => setDismissed(true)}
      title="Your access has been updated"
      size="sm"
      showCloseButton={false}
    >
      <p className="text-sm text-palette-gray-badge mb-6">
        An organizer changed your role or clinic assignments. Please log out and sign back in to see
        your updated permissions.
      </p>
      <div className="flex items-center justify-end gap-3">
        <Button variant="outline" size="sm" onClick={() => setDismissed(true)}>
          Later
        </Button>
        <Button size="sm" onClick={handleLogout}>
          Log out now
        </Button>
      </div>
    </Modal>
  );
}
